import React, { ReactElement, useContext, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { StatusCodes } from 'http-status-codes';
import { Button, CircularProgress, Divider, SvgIcon, Typography } from '@material-ui/core';
import { ResponseError } from 'superagent';
import classNames from 'classnames';
import { Link } from 'react-router-dom';
import PageContent from 'components/Generic/PageContent';
import { Routes } from 'routes';
import { TableFilterProvider } from 'tableFilterContext';
import { AppContext } from 'context';
import { transformObjectId } from 'services/apiPlatformTransformer';
import client from 'services/client';
import { notify } from 'components/Notifier';
import Dialog from 'components/Dialog';
import { DialogWidth, Event, ReceptionFilters, ValidateViolation } from 'types';
import ValidateIcon from 'components/Icons/ValidateIcon';
import Loading from 'components/Loading';
import ClosureErrorIcon from 'components/Icons/ClosureErrorIcon';
import { DownloadIcon } from 'components/Icons';
import { getImportReceptionsReport } from 'services/apiOperations';
import ReceptionList from './ReceptionList';
import { ImportSuccessResponse } from './types';
import { downloadReceptions } from './services';
import { useStyles } from './index.styles';

function getViolationLines(violations?: ValidateViolation[]): number[] {
  if (!violations) return [];
  const lines = new Set(
    violations
      .filter(violation => !!violation?.parameters?.['lineNumberInCsv'])
      .map(violation => Number(violation?.parameters?.['lineNumberInCsv'] as string)),
  );
  return Array.from(lines).sort((a, b) => a - b);
}

const ReceptionListPage: React.FC = () => {
  const { t } = useTranslation();
  const classes = useStyles();
  const [isImportButtonDisabled, setIsImportButtonDisabled] = useState(false);
  const [isImportSuccess, setIsImportSuccess] = useState(false);
  const [isImportFailed, setIsImportFailed] = useState(false);
  const [isDisplayDivider, setIsDisplayDivider] = useState(false);
  const [isImportRun, setIsImportRun] = useState(false);
  const [isOpenImportDialog, setIsOpenImportDialog] = useState(false);
  const [importDialogTitle, setImportDialogTitle] = useState<ReactElement>(<></>);
  const [importDialogMessage, setImportDialogMessage] = useState<ReactElement>(<></>);
  const [downloadModalVisible, setDownloadModalVisible] = useState<boolean>(false);
  const context = useContext(AppContext);
  const sortingCenter = context.getSortingCenter?.();
  const sortingCenterId = sortingCenter?.['@id'].split('/')[3];
  const [filter, setFilter] = useState<ReceptionFilters | null>({});
  const [isExportDisabled, setExportDisabled] = useState<boolean>(true);
  const [importErrorMessage, setImportErrorMessage] = useState<string>('');

  const onChangeImportFile = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (!sortingCenter || !e.target.files) return;

    setImportDialogTitle(t('receptions.import.analysisInProgressTitle'));
    setImportDialogMessage(t('receptions.import.analysisInProgressMessage'));
    setIsImportButtonDisabled(true);
    setIsOpenImportDialog(true);
    setIsImportSuccess(false);
    setIsImportFailed(false);
    setIsDisplayDivider(false);
    setIsImportRun(true);
    setImportErrorMessage('');

    client
      .postFile(`/sorting_centers/${transformObjectId(sortingCenter)}/receptions/import`, e.target.files[0])
      .then((successResponse: ImportSuccessResponse) => {
        setIsImportButtonDisabled(false);
        setIsImportSuccess(true);
        setIsImportRun(false);
        setImportDialogTitle(t('receptions.import.successTitle'));
        setImportDialogMessage(
          t('receptions.import.successMessage', { numberOfLines: successResponse.numberOfLinesImported.toString() }),
        );
        PubSub.publish(Event.REFRESH_RECEPTIONS);
      })
      .catch((errorResponse: ResponseError) => {
        const errorStatus = client.getErrorResponseStatus(errorResponse);
        const constraintViolationList = client.getConstraintViolationListFromApiPlatform(errorResponse);

        setIsImportButtonDisabled(false);
        setIsImportRun(false);
        setIsImportFailed(true);

        switch (errorStatus) {
          case StatusCodes.PRECONDITION_FAILED:
            setIsDisplayDivider(true);
            setImportDialogTitle(
              <div className={classes.errorContainer}>
                <ClosureErrorIcon className={classes.errorIcon} />
                <Typography className={classes.errorTitle}>{t('receptions.import.failedTitle')}</Typography>
              </div>,
            );
            setImportDialogMessage(
              <div className={classes.errorList}>
                <ul style={{ display: 'inline-block' }}>
                  {getViolationLines(constraintViolationList?.violations).map((line: number) => {
                    return (
                      <li key={line}>
                        {t('receptions.import.failedMessage', {
                          lineNumberInCsv: line,
                        })}
                      </li>
                    );
                  })}
                </ul>
              </div>,
            );
            break;
          case StatusCodes.PRECONDITION_REQUIRED:
            setImportDialogTitle(
              <div className={classes.errorContainer}>
                <ClosureErrorIcon className={classes.errorIcon} />
                <Typography className={classes.errorTitle}>
                  {t('receptions.import.errorTypes.ERROR_MISSING_HEADER')}
                </Typography>
              </div>,
            );
            setImportDialogMessage(
              <div className={classes.withoutBold}>
                {t('receptions.import.badFormatMessage')}{' '}
                <Link to="/documents">{t('receptions.import.badFormatMessageLinkLabel')}</Link>
              </div>,
            );
            break;
          case StatusCodes.UNSUPPORTED_MEDIA_TYPE:
            setImportDialogTitle(
              <div className={classes.errorContainer}>
                <ClosureErrorIcon className={classes.errorIcon} />
                <Typography className={classes.errorTitle}>
                  {t('receptions.import.errorTypes.ERROR_FILE_FORMAT')}
                </Typography>
              </div>,
            );
            setImportDialogMessage(
              <div className={classes.withoutBold}>
                {t('receptions.import.badFormatMessage')}{' '}
                <Link to="/documents">{t('receptions.import.badFormatMessageLinkLabel')}</Link>
              </div>,
            );
            break;
          case StatusCodes.NOT_ACCEPTABLE:
            setImportDialogTitle(
              <div className={classes.errorContainer}>
                <ClosureErrorIcon className={classes.errorIcon} />
                <Typography className={classes.errorTitle}>
                  {t('receptions.import.errorTypes.ERROR_FILE_ENCODING')}
                </Typography>
              </div>,
            );
            setImportDialogMessage(
              <div className={classes.withoutBold}>
                {t('receptions.import.badFormatMessage')}{' '}
                <Link to="/documents">{t('receptions.import.badFormatMessageLinkLabel')}</Link>
              </div>,
            );
            break;
          case StatusCodes.BAD_REQUEST:
            setImportDialogTitle(
              <div className={classes.errorContainer}>
                <ClosureErrorIcon className={classes.errorIcon} />
                <Typography className={classes.errorTitle}>
                  {t('receptions.import.errorTypes.ERROR_VALIDATION')}
                </Typography>
              </div>,
            );
            setImportDialogMessage(
              <div className={classes.withoutBold}>
                {t('receptions.import.badFormatMessage')}{' '}
                <Link to="/documents">{t('receptions.import.badFormatMessageLinkLabel')}</Link>
              </div>,
            );
            break;
          default:
            setIsOpenImportDialog(false);
            notify({
              message: t('errorGeneric'),
              severity: 'error',
            });
        }
        constraintViolationList?.violations.forEach(violation => {
          setImportErrorMessage(errors => {
            if (violation.parameters?.code === 'ERROR_DATE_POSTERIOR') {
              return (
                errors +
                `Ligne ${violation.parameters?.lineNumberInCsv} - ` +
                `${t('receptions.import.errorLineTypes.ERROR_VALIDATION_DATETIME', {
                  inputValue: violation.parameters?.inputValue,
                })}<br/>`
              );
            } else {
              return (
                errors +
                `Ligne ${violation.parameters?.lineNumberInCsv} - ` +
                `${t('receptions.import.errorLineTypes.' + violation.parameters?.code, {
                  inputValue: violation.parameters?.inputValue,
                })}<br/>`
              );
            }
          });
        });
      });

    e.target.value = ''; // Trigger onChange event with same file / Allow to select the same file again
  };

  const handleClickCloseFailedImportDialog = () => {
    setIsOpenImportDialog(false);
    notify({
      message: t('receptions.import.operationCanceled'),
      severity: 'warning',
    });
  };

  const handleClickCloseSuccessImportDialog = () => {
    setIsOpenImportDialog(false);
    notify({
      message: t('receptions.import.fileDownloaded'),
      severity: 'success',
    });
  };

  const handleClickDownloadErrorReport = () => {
    getImportReceptionsReport(sortingCenterId ?? '', importErrorMessage).then(() => {
      setIsOpenImportDialog(false);
      notify({
        message: t('receptions.import.fileDownloaded'),
        severity: 'success',
      });
    });
  };

  return (
    <PageContent
      title={t('receptions.entityName')}
      actionButtonLink={{ pathname: Routes.ReceptionCreate, state: { referer: Routes.Receptions } }}
      importButtonHandler={onChangeImportFile}
      importButtonDisabled={isImportButtonDisabled}
      exportButtonHandler={() => {
        if (!sortingCenterId) return;
        downloadReceptions(sortingCenterId, filter ?? {}, t, setDownloadModalVisible);
      }}
      exportButtonDisabled={isExportDisabled}
      noPadding
    >
      <Dialog
        closable={false}
        renderButton={() => {}}
        isOpen={downloadModalVisible}
        width={DialogWidth.SMALL_FORM}
        renderContent={() => (
          <>
            <h1
              style={{
                fontWeight: 600,
                fontSize: 24,
              }}
            >
              {t('receptions.export.title')}
            </h1>
            <h2 className={classes.downloadModalSubtitle}>{t('receptions.export.subtitle')}</h2>
            <CircularProgress style={{ marginBottom: 10 }} size={49} thickness={6} />
          </>
        )}
      />
      <Dialog
        closable={isImportSuccess}
        renderButton={() => {}}
        isOpen={isOpenImportDialog}
        onClose={handleClickCloseSuccessImportDialog}
        width={DialogWidth.LARGE_FORM}
        title={<Typography className={classes.title}>{importDialogTitle}</Typography>}
        renderContent={() => (
          <div style={{ paddingBottom: '15px' }}>
            {isDisplayDivider && <Divider className={classes.tableDivider} />}
            <div className={classes.dialogMessage}>
              <div
                className={classNames({
                  [classes.withoutMarginBottom]: isImportFailed,
                })}
              >
                {importDialogMessage}
              </div>
              <div>
                {isImportRun && <Loading naked />}

                {isImportSuccess && <SvgIcon component={ValidateIcon} color="primary" className={classes.icon} />}
              </div>
            </div>
            {isDisplayDivider && <Divider className={classes.tableDivider} />}
            {isImportFailed && (
              <>
                {importErrorMessage && (
                  <div className={classes.downloadText}>{t('receptions.import.downloadText')}</div>
                )}
                <Button
                  className={classes.closeButton}
                  variant="outlined"
                  startIcon={<ClosureErrorIcon className={classes.closeIcon} />}
                  onClick={handleClickCloseFailedImportDialog}
                >
                  {t('close')}
                </Button>
                {importErrorMessage && (
                  <Button
                    className={classes.downloadButton}
                    variant="outlined"
                    endIcon={<DownloadIcon className={classes.closeIcon} />}
                    onClick={handleClickDownloadErrorReport}
                  >
                    {t('receptions.import.download')}
                  </Button>
                )}
              </>
            )}
          </div>
        )}
      />
      <TableFilterProvider>
        <ReceptionList filter={filter} setFilter={setFilter} setExportDisabled={setExportDisabled} />
      </TableFilterProvider>
    </PageContent>
  );
};
export default ReceptionListPage;
